import { useEffect, useState, useSyncExternalStore } from 'react';
import { brand } from '@app/shared/brand';
import { getPrefs, updatePrefs, usePrefs } from './prefs.ts';

/**
 * Optional app lock using the device's own screen lock (Windows Hello, Touch
 * ID / Face ID, Android fingerprint or PIN) through WebAuthn's platform
 * authenticator. It is a privacy screen for a shared or borrowed device: it
 * hides the app until the device owner verifies. It does not encrypt the data
 * stored on the device.
 */

const LOCK_AFTER_HIDDEN_MS = 5 * 60_000;

const toB64 = (buf: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
const fromB64 = (s: string) => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};
const randomChallenge = () => crypto.getRandomValues(new Uint8Array(new ArrayBuffer(32)));

export async function appLockSupported(): Promise<boolean> {
  try {
    return typeof PublicKeyCredential !== 'undefined' && (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
  } catch {
    return false;
  }
}

export async function enableAppLock(user: { id: string; displayName: string; email: string | null }): Promise<void> {
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: randomChallenge(),
      rp: { name: brand.productName, id: location.hostname },
      user: { id: new TextEncoder().encode(user.id), name: user.email ?? user.displayName, displayName: user.displayName },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'discouraged' },
      timeout: 60_000,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('The device did not create a lock.');
  updatePrefs((p) => ({ ...p, security: { appLockCredentialId: toB64(cred.rawId) } }));
}

export function disableAppLock() {
  updatePrefs((p) => ({ ...p, security: { appLockCredentialId: null } }));
}

async function verifyDeviceOwner(credentialId: string): Promise<boolean> {
  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: randomChallenge(),
        allowCredentials: [{ id: fromB64(credentialId), type: 'public-key', transports: ['internal'] }],
        userVerification: 'required',
        timeout: 60_000,
      },
    });
    return assertion !== null;
  } catch {
    return false;
  }
}

let locked = false;
let hiddenAt: number | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function lockNow() {
  if (getPrefs().security.appLockCredentialId) {
    locked = true;
    emit();
  }
}

/** Lock state for the signed-in shell: locked at start and after five minutes in the background. */
export function useAppLock() {
  const credentialId = usePrefs().security.appLockCredentialId;
  const isLocked = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => locked,
  );
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      if (credentialId) lockNow();
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt > LOCK_AFTER_HIDDEN_MS) lockNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [credentialId, initialized]);
  return {
    enabled: Boolean(credentialId),
    locked: Boolean(credentialId) && isLocked,
    async unlock(): Promise<boolean> {
      if (!credentialId) return true;
      const ok = await verifyDeviceOwner(credentialId);
      if (ok) {
        locked = false;
        emit();
      }
      return ok;
    },
  };
}
